import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import { useNavigate } from 'react-router-dom';
import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';

import { AdminSearchInsideSection, useSearchInsideSectionLazy } from '@SERVICES';
import { containsSearchTerm, extractContentSnippets } from '@HELPERS/textHighlighting';

import scrollToVariant from '@PAGES/admin/edit-section/helpers/scrollToVariant';

import ResultSetWrapper from '@COMPONENTS/HEADERS/breadcrumbs/AdminBreadclumbs/components/ResultSetWrapper';
import SearchResultCard from '@COMPONENTS/HEADERS/breadcrumbs/AdminBreadclumbs/components/SearchResultCard';
import ContentMatch from '@COMPONENTS/HEADERS/breadcrumbs/AdminBreadclumbs/components/ContentMatch';

import styles from './SectionResults.module.scss';

const cx: CX = classnames.bind(styles);

function SectionResults(props: Props) {
    const {
        id,
        q,
        setLoading,
        isFocused,
        setFocused,
        searchValue,
        onSearchChange,
        isSearchLoading,
        searchPlaceholder,
    } = props;

    const [
        searchInsideSection,
        {
            data, isLoading, requestId,
        },
    ] = useSearchInsideSectionLazy();

    const navigate = useNavigate();

    const [results, setResults] = useState<AdminSearchInsideSection | undefined>(undefined);

    const showResultSet = (
        q.length > 2
        && results !== undefined
        && isFocused
    );

    useLayoutEffect(() => {
        setLoading(isLoading);
    }, [setLoading, isLoading]);

    useLayoutEffect(() => {
        if (data) {
            setResults(data);
        }
    }, [data, requestId]);

    useLayoutEffect(() => {
        if (q.length <= 2) {
            setResults(undefined);
        }
    }, [q]);

    useEffect(() => {
        if (q.trim().length > 2) {
            const result = searchInsideSection({
                id,
                q,
            });

            return () => {
                result.abort();
            };
        }
    }, [id, q, searchInsideSection]);

    if (!showResultSet) return null;

    function renderSearchResults() {
        if (!results) return null;

        const searchResults: JSX.Element[] = [];

        // Check section name match
        if (containsSearchTerm(results.data.name, q)) {
            searchResults.push(
                <SearchResultCard
                    key={`section-${results.data.id}`}
                    title={results.data.name}
                    type="section"
                    searchTerm={q}
                    onClick={() => {
                        setFocused(false);
                        navigate(ROUTES.admin.sections.id.get(results.data.id));
                    }}
                />
            );
        }

        // Check section content match
        if (results.data.body) {
            const sectionContentSnippets = extractContentSnippets(results.data.body, q);
            if (sectionContentSnippets.length > 0) {
                searchResults.push(
                    <ContentMatch
                        key={`section-content-${results.data.id}`}
                        title={results.data.name}
                        type="section"
                        snippets={sectionContentSnippets}
                        onClick={() => {
                            setFocused(false);
                            navigate(ROUTES.admin.sections.id.get(results.data.id));
                        }}
                        metadata="Introduction text"
                    />
                );
            }
        }

        // Process variants
        results.data.variants.forEach((variant) => {
            const variantNameMatch = containsSearchTerm(variant.name, q);
            const variantContentSnippets = extractContentSnippets(variant.body, q);

            // Show name match for variant
            if (variantNameMatch) {
                searchResults.push(
                    <SearchResultCard
                        key={`variant-name-${variant.id}`}
                        title={variant.name}
                        type="variant"
                        searchTerm={q}
                        onClick={() => {
                            setFocused(false);
                            navigate(ROUTES.admin.sections.id.get(results.data.id, variant.id));
                            setTimeout(() => {
                                scrollToVariant(variant.id);
                            }, 150);
                        }}
                        metadata={`in ${results.data.name}`}
                    />
                );
            }

            // Show content matches for variant
            if (variantContentSnippets.length > 0) {
                searchResults.push(
                    <ContentMatch
                        key={`variant-content-${variant.id}`}
                        title={variant.name}
                        type="variant"
                        snippets={variantContentSnippets}
                        onClick={() => {
                            setFocused(false);
                            navigate(ROUTES.admin.sections.id.get(results.data.id, variant.id));
                            setTimeout(() => {
                                scrollToVariant(variant.id);
                            }, 150);
                        }}
                        metadata={`in ${results.data.name}`}
                    />
                );
            }
        });

        return searchResults;
    }

    const searchResults = renderSearchResults();

    return (
        <ResultSetWrapper
            setFocused={setFocused}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            isSearchLoading={isSearchLoading}
            searchPlaceholder={searchPlaceholder}
        >
            <div className={cx('section-results')}>
                {searchResults && searchResults.length > 0 ? (
                    <div className={cx('results-list')}>
                        {searchResults}
                    </div>
                ) : (
                    <div className={cx('empty-state')}>
                        <div className={cx('empty-icon')}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                            >
                                <circle cx="11" cy="11" r="8" />
                                <path d="21 21l-4.35-4.35" />
                                <path d="11 8v6" />
                                <path d="8 11h6" />
                            </svg>
                        </div>
                        <div className={cx('empty-title')}>
                            No results found
                        </div>
                        <div className={cx('empty-description')}>
                            We couldn&apos;t find any content matching &quot;{searchValue}&quot;. Try adjusting your search terms or browse the section structure instead.
                        </div>
                    </div>
                )}
            </div>
        </ResultSetWrapper>
    );
}

SectionResults.propTypes = {
    id: PropTypes.number.isRequired,
    q: PropTypes.string.isRequired,
    setLoading: PropTypes.func.isRequired,
    isFocused: PropTypes.bool.isRequired,
    setFocused: PropTypes.func.isRequired,
    searchValue: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    isSearchLoading: PropTypes.bool.isRequired,
    searchPlaceholder: PropTypes.string.isRequired,
};

type Props = InferProps<typeof SectionResults.propTypes>;

export default SectionResults;
